// 디자인 토큰을 캔버스가 먹는 값으로 푼다.
//
// uPlot 은 캔버스에 그린다. 캔버스는 CSS 를 모르므로 `var(--chart-1)` 을 그대로 줄 수 없다 —
// 그 자리에서 **지금 테마의 값**으로 풀어 넘겨야 하고, 테마가 바뀌면 다시 풀어 다시 그려야
// 한다. 색을 JS 에 적어 두면 이 두 일이 필요 없어 보이지만, 그 순간 토큰과 갈라진다
// (CLAUDE.md 「컴포넌트에 색을 리터럴로 적지 않는다」).

// tokenName 은 '--chart-1' · 'var(--chart-1)' · 'chart-1' 을 모두 '--chart-1' 로 맞춘다.
export function tokenName(ref) {
  const s = String(ref).trim()
  const m = /^var\(\s*(--[\w-]+)\s*\)$/.exec(s)
  if (m) return m[1]
  return s.startsWith('--') ? s : `--${s}`
}

// makeResolver 는 el 안에 보이지 않는 탐침 요소를 하나 두고, 토큰을 그 요소의 계산된
// 스타일로 푼다.
//
// getPropertyValue('--x') 로 바로 읽지 않는 이유: 토큰이 다른 토큰을 부르거나(var 안의
// var) calc·color-mix 를 쓰면 원문 문자열이 나온다. 탐침의 color·font 로 한 번 적용시켜
// 읽으면 브라우저가 끝까지 계산한 값(rgb(…), px)이 나온다. 글자 램프(--t-*)는 font 단축
// 속성 값이라 캔버스 font 문자열로 바로 쓸 수 없다 — 탐침에 적용해 풀어 읽는다.
export function makeResolver(el) {
  const probe = document.createElement('span')
  probe.className = 'mops-chart__probe'
  probe.setAttribute('aria-hidden', 'true')
  el.appendChild(probe)
  const cache = new Map()

  function color(ref) {
    const name = tokenName(ref)
    if (!cache.has(name)) {
      probe.style.color = `var(${name})`
      cache.set(name, getComputedStyle(probe).color)
    }
    return cache.get(name)
  }

  // font 는 캔버스 font 문자열로 푼다. 램프 이름은 '--t-mono-sm' 꼴.
  function font(ref) {
    const name = `font:${tokenName(ref)}`
    if (!cache.has(name)) {
      probe.style.font = `var(${tokenName(ref)})`
      const cs = getComputedStyle(probe)
      cache.set(name, `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`)
    }
    return cache.get(name)
  }

  // length 는 치수 토큰(--radius-sm 등)을 px 숫자로 푼다. 잔디 칸의 둥근 모서리가 이것을 쓴다 —
  // 모서리를 숫자로 박으면 HTML 칸(범례)과 캔버스 칸(판)의 둥글기가 갈린다.
  function length(ref) {
    const name = `len:${tokenName(ref)}`
    if (!cache.has(name)) {
      probe.style.borderTopLeftRadius = `var(${tokenName(ref)})`
      cache.set(name, parseFloat(getComputedStyle(probe).borderTopLeftRadius) || 0)
    }
    return cache.get(name)
  }

  return {
    color,
    font,
    length,
    // 테마가 바뀌면 캐시를 비운다 — 다음 그리기에서 새 값으로 다시 푼다.
    reset: () => cache.clear(),
    dispose: () => probe.remove(),
  }
}

// watchTheme 는 테마가 바뀔 수 있는 자리를 모두 지켜보다가 onChange 를 부른다.
//
//   · <html> 의 data-theme · class · style — 테마 토글이 여기 앉는다(v3 는 밀도 배수가 없다)
//   · <body> 의 class — .mops 를 붙였다 떼는 화면이 있다
//   · 시스템 테마(prefers-color-scheme) — data-theme 이 없을 때 이걸 따른다
export function watchTheme(onChange) {
  const mo = new MutationObserver(onChange)
  const opts = { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] }
  mo.observe(document.documentElement, opts)
  if (document.body) mo.observe(document.body, opts)
  const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null
  mq?.addEventListener?.('change', onChange)
  return () => {
    mo.disconnect()
    mq?.removeEventListener?.('change', onChange)
  }
}
